import React, { useState, useEffect, useRef } from "react";

function pad2(n) { return String(n).padStart(2, "0"); }

// 24-hour time input (HH:MM). Plain text — NOT <input type="time"> — so it
// always shows 24h regardless of browser locale (type=time renders AM/PM in
// US locale). Accepts "1530", "15:30", "930", etc.; commits a valid HH:MM
// upstream and tolerates partial typing.
//
// The focus guard is essential: a live commit mid-typing (e.g. "150" parses to
// 01:50 before the 4th digit lands) would otherwise echo the parent value back
// and clobber what's being typed, stranding entry at the wrong time.
export default function Time24Field({ value, onChange, title, style, placeholder = "HH:MM" }) {
  const [raw, setRaw] = useState(value || "");
  const focusedRef = useRef(false);
  useEffect(() => { if (!focusedRef.current) setRaw(value || ""); }, [value]);

  function normalize(s) {
    const t = String(s).trim();
    if (!t) return null;
    let h, m;
    const colon = t.match(/^(\d{1,2}):(\d{2})$/);
    if (colon) { h = +colon[1]; m = +colon[2]; }
    else {
      const digits = t.replace(/\D/g, "");
      if (digits.length === 3) { h = +digits.slice(0, 1); m = +digits.slice(1); }
      else if (digits.length === 4) { h = +digits.slice(0, 2); m = +digits.slice(2); }
      else return null; // not enough typed yet to parse
    }
    if (h > 23 || m > 59) return null;
    return `${pad2(h)}:${pad2(m)}`;
  }

  function commit(next) {
    if (!String(next).trim()) { onChange(""); return; } // cleared
    const norm = normalize(next);
    if (norm != null) onChange(norm); // only push a valid HH:MM upstream
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      maxLength={5}
      value={raw}
      title={title}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => { setRaw(e.target.value); commit(e.target.value); }}
      onBlur={() => {
        focusedRef.current = false;
        const n = normalize(raw);
        if (n != null) setRaw(n);                 // clean up display to HH:MM
        else if (raw.trim()) setRaw(value || ""); // revert garbage to last committed
      }}
      style={style}
    />
  );
}
